<script setup lang="ts">
import { computed } from 'vue';
import type { Traveler } from '@/types/Traveler.ts';
import CheckoutSidebarCard from './CheckoutSidebarCard.vue';
import { $t } from '@/composables/translations/useTranslate';
import { getCountryName } from '@/constants/checkout/countries';

interface Props {
  travelers?: Traveler[];
  showEditButton?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  travelers: () => [],
  showEditButton: false,
});

const emit = defineEmits<{
  edit: [];
}>();

function handleEdit() {
  emit('edit');
}

function getSalutation(salutation?: 'mr' | 'mrs' | 'diverse'): string {
  if (salutation === 'mr') return $t('ibe.form.salutation.mr', 'Mr');
  if (salutation === 'mrs') return $t('ibe.form.salutation.mrs', 'Mrs');
  return '';
}

const VARIANT_LABEL: Record<Traveler['type'], { key: string; fallback: string }> = {
  adult: { key: 'ibe.checkout.traveler.adult-label', fallback: 'Traveler {number}' },
  under18adult: { key: 'ibe.checkout.traveler.youth-label', fallback: 'Traveler {number}' },
  child: { key: 'ibe.checkout.traveler.child-label', fallback: 'Child {number}' },
  infant: { key: 'ibe.checkout.traveler.infant-label', fallback: 'Infant {number}' },
};

/**
 * Computed property that maps each traveler to their display label with numbering.
 * Each variant is numbered separately (Traveler 1, Reisender 1, Child 1, Baby 1, …).
 */
const travelerLabels = computed(() => {
  const labels = new Map<string | number, string>();
  const counts: Record<Traveler['type'], number> = {
    adult: 0,
    under18adult: 0,
    child: 0,
    infant: 0,
  };

  for (const traveler of props.travelers) {
    const { key, fallback } = VARIANT_LABEL[traveler.type];
    counts[traveler.type]++;
    labels.set(traveler.id, $t(key, fallback, { number: counts[traveler.type] }));
  }

  return labels;
});

function getTravelerLabel(travelerId: string | number): string {
  return (
    travelerLabels.value.get(travelerId) || $t('ibe.checkout.traveler.adult-label', 'Traveler')
  );
}

function formatLocation(traveler: Traveler): string {
  const parts: string[] = [];
  if (traveler.postalCode) parts.push(traveler.postalCode);
  if (traveler.location) parts.push(traveler.location);
  if (traveler.country) {
    parts.push(getCountryName(traveler.country));
  }
  return parts.join(' ');
}
</script>

<template>
  <CheckoutSidebarCard
    :show-edit-button="props.showEditButton"
    :rounded-top="true"
    wrapper-class="mt-2.5 lg:max-w-[435px]"
    @edit="handleEdit"
  >
    <div
      v-for="traveler in props.travelers"
      :key="traveler.id"
      class="flex flex-col gap-[5px] mb-5"
    >
      <p class="text-body-small mb-[5px]">{{ getTravelerLabel(traveler.id) }}</p>

      <p class="text-body-large">
        {{ getSalutation(traveler.salutation) }}
        {{ traveler.firstName }}
        {{ traveler.lastName }}
      </p>

      <p v-if="traveler.birthDate" class="text-body">{{ traveler.birthDate }}</p>

      <p v-if="traveler.street" class="text-body">{{ traveler.street }}</p>
      <p v-if="formatLocation(traveler)" class="text-body">{{ formatLocation(traveler) }}</p>

      <p v-if="traveler.mobile" class="text-body">
        <span class="text-action">{{ $t('ibe.form.label.mobile', 'Mobile phone') }}:</span>
        {{ traveler.mobile }}
      </p>
      <p v-if="traveler.email" class="text-body">
        <span class="text-action">{{ $t('ibe.form.label.email', 'Email address') }}:</span>
        {{ traveler.email }}
      </p>
    </div>

    <!-- Empty State -->
    <div v-if="props.travelers.length === 0" class="py-5 text-center">
      <p class="text-body text-text-primary/60">
        {{ $t('ibe.checkout.summary.no-travelers', 'No travelers added') }}
      </p>
    </div>
  </CheckoutSidebarCard>
</template>
